<script setup lang="ts">
import { ref, computed, provide } from 'vue';
import SideNav from './SideNav.vue';
import TopBar from './TopBar.vue';
import PublicationReminderBanner from './PublicationReminderBanner.vue';
import QuickAddOverlay from '@/components/quick-add/QuickAddOverlay.vue';
import QuickAddFab from '@/components/quick-add/QuickAddFab.vue';
import { UndoToast, CommandPalette, KeyboardShortcutsModal } from '@/components/shared';
import { useFeatures } from '@/composables/useFeatures';
import { useKeyboardShortcut } from '@/composables/useKeyboardShortcut';
import { useCommandPaletteStore } from '@/stores/commandPaletteStore';
import { useKeyboardShortcutsStore } from '@/stores/keyboardShortcutsStore';

const sideNavCollapsed = ref(localStorage.getItem('sideNavCollapsed') === 'true');

function toggleSideNav() {
  sideNavCollapsed.value = !sideNavCollapsed.value;
  localStorage.setItem('sideNavCollapsed', String(sideNavCollapsed.value));
}

provide('sideNavCollapsed', sideNavCollapsed);

// E14: feature-flag the quick-add overlay so it can be flipped on/off from
// config/.env without a code change. Overlay and FAB are both conditional
// on the same flag.
const { flags } = useFeatures();
const quickAddEnabled = computed(() => flags.value.quickAdd === true);

// NS-3: command palette (⌘K / Ctrl+K). Reuses the globalSearch feature
// flag — same backend endpoint as the TopBar dropdown, so if search is
// off the palette is too. Toggle bound to both Ctrl+K and Cmd+K via
// `ctrlOrMeta: true`. Fires even when focus is inside an input because
// that's the modern-SaaS expectation — the user should be able to hit
// ⌘K from anywhere.
const commandPaletteEnabled = computed(() => flags.value.globalSearch === true);
const commandPalette = useCommandPaletteStore();

useKeyboardShortcut({
  key: 'k',
  ctrlOrMeta: true,
  allowInInput: true,
  handler: (e) => {
    if (!commandPaletteEnabled.value) return;
    e.preventDefault();
    commandPalette.toggle();
  },
});

// NS-4: `?` opens the keyboard-shortcut cheat sheet. Two bindings
// because keyboard event semantics differ across engines:
//
//   - Real Chrome / Firefox: Shift+/ fires keydown with `key: '?'`
//     (OS-level shift remap applied), so `{ key: '?' }` matches.
//   - Playwright's Chromium (and some synthetic key events): Shift+/
//     fires keydown with `key: '/'` + `shiftKey: true` with NO remap,
//     so we also need `{ key: '/', shift: true }` for tests + any
//     headless-browser contexts that behave the same way.
//
// Both bindings call `openModal()` (idempotent) rather than `toggle()`
// so the rare double-fire (if both ever trigger on the same physical
// keystroke) is a no-op. Close is via Esc / click-outside / close button.
// `allowInInput: false` (default) keeps the shortcut scoped out of
// text inputs — typing a literal `?` in the search box does NOT open
// the modal.
const keyboardShortcuts = useKeyboardShortcutsStore();
function openShortcutsModal(e: KeyboardEvent) {
  e.preventDefault();
  keyboardShortcuts.openModal();
}
useKeyboardShortcut({ key: '?', handler: openShortcutsModal });
useKeyboardShortcut({ key: '/', shift: true, handler: openShortcutsModal });
</script>

<template>
  <div class="app-shell grid grid-rows-[auto_1fr] min-h-screen bg-surface-950 text-text-primary">
    <!-- Top Bar (full width) -->
    <TopBar class="col-span-full" @toggle-sidenav="toggleSideNav" />

    <!-- Below top bar: SideNav + Main content -->
    <div class="app-body grid lg:grid-cols-[auto_1fr]">
      <SideNav :collapsed="sideNavCollapsed" class="hidden lg:flex" />

      <main class="app-main overflow-y-auto h-[calc(100vh-57px)]">
        <PublicationReminderBanner />
        <slot />
      </main>
    </div>

    <!-- E14: Quick-add overlay + floating button. Both are gated on the
         feature flag AND (inside their own components) the user's role. -->
    <template v-if="quickAddEnabled">
      <QuickAddOverlay />
      <QuickAddFab />
    </template>

    <!-- NS-3: Command palette (⌘K). Gated on the same globalSearch flag
         that powers the TopBar dropdown — one backend, two surfaces. -->
    <CommandPalette v-if="commandPaletteEnabled" />

    <!-- NS-4: Keyboard-shortcut cheat sheet (?). Always mounted — no
         feature flag because "list every shortcut that exists right
         now" is useful regardless of which features are flipped on. -->
    <KeyboardShortcutsModal />

    <!-- Global undo toast — singleton, controlled by useUndoToast() -->
    <UndoToast />
  </div>
</template>
